/**
 * Created: 2026-09-01
 * Initial commit: Share computeDisplayHours between web SlotsPage and mini admin site page
 *
 * Computes the contiguous hour range to display in a slot grid, shared
 * between the web admin app and the WeChat mini-program admin site page.
 */

const WEEKDAY_NAMES = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const

/**
 * Convert a Date to the backend weekday name (0=Mon convention), matching
 * Date.getDay() (0=Sun…6=Sat) via (getDay + 6) % 7.
 */
function dateToWeekdayName(date: Date): string {
  return WEEKDAY_NAMES[(date.getDay() + 6) % 7]
}

/**
 * Compute the contiguous display hour range for a slot grid.
 *
 * Takes the union of:
 *  - hours derived from actual slot start times (catches historical bookings
 *    outside today's template range, e.g. slots booked when courts had longer
 *    hours due to a seasonal schedule change)
 *  - hours from slot templates matching the selected date's weekday (ensures
 *    the full operating window is shown even for past dates with no OPEN slots,
 *    and handles multi-sport sites where courts have different opening hours)
 *
 * Returns a contiguous array [hourStart, …, hourEnd]. Returns [] when both
 * inputs are empty.
 */
export function computeDisplayHours(
  slotStartTimes: string[],
  templates: { weekday: string; hour: number }[],
  date: Date,
): number[] {
  let min = Number.POSITIVE_INFINITY
  let max = Number.NEGATIVE_INFINITY

  for (const startTime of slotStartTimes) {
    const chinaHour = (new Date(startTime).getUTCHours() + 8) % 24
    min = Math.min(min, chinaHour)
    max = Math.max(max, chinaHour)
  }

  const weekdayName = dateToWeekdayName(date)
  for (const t of templates) {
    if (t.weekday === weekdayName) {
      min = Math.min(min, t.hour)
      max = Math.max(max, t.hour)
    }
  }

  if (min === Number.POSITIVE_INFINITY) return []
  return Array.from({ length: max - min + 1 }, (_, i) => min + i)
}
